// @dynamic-i18n-keys
//   packagePicker.billingCycle.new.subHeading.core_access
//   packagePicker.billingCycle.new.subHeading.core_plus
//   packagePicker.billingCycle.new.subHeading.core_pro
//   packagePicker.billingCycle.new.subHeading.core_pro_plus
//   packagePicker.billingCycle.new.subHeading.core_max
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiProductV3, PapiUser } from '@wix/da-papi-types';
import DSModal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import { BiData, BuyCoreButtonClickFromPPBiEvent } from '@wix/da-bi/pkg/events';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import ListItemCardRadioButton from '@wix/da-ds/pkg/List/ListItemCard/dsPresets/ListItemCardRadioButton';
import { useRestrictToLoggedIn } from '@wix/da-shared-react/pkg/utils/hooks/useRestrictToLoggedIn';
import { ReferralType } from '@wix/da-bi/pkg/constants';
import { CoreTypes } from '../../types';
import { PackagePickerStoreSlice } from '../../../../redux/packagePicker/types';
import { formatSaleEndDate } from '../../utils';
import PackageSavingsLabel from './_partials/PackageSavingsLabel';
import PackageSublabel from './_partials/PackageSublabel';
import PackageLabel from './_partials/PackageLabel';
import SimpleTooltip from '@wix/da-ds/pkg/tooltips/dsPreset/SimpleTooltip';

import s from './BillingCyclePopup.scss';

export interface Props {
  coreType: CoreTypes;
  className?: string;
  withCloseButton?: boolean;
  isOpen: boolean;
  onRequestClose?: () => void;
  selectedPackage?: number[]; // sub product ids for coreType
  filteredPackageProducts?: PapiProductV3[]; // Package products for selected core type
  giftUser?: PapiUser;
  sale?: PackagePickerStoreSlice['sale'];
  alreadyHasSubscription?: boolean;
}

const BillingCyclePopup: FC<Props> = ({
  isOpen,
  coreType,
  giftUser,
  sale,
  onRequestClose,
  filteredPackageProducts: packageProducts = [],
  alreadyHasSubscription,
}) => {
  const { t, i18n } = useTranslation();
  const { restrictToLoggedIn } = useRestrictToLoggedIn();
  const [selectedCycle, setSelectedCycle] = useState('year');

  const monthlyPackageProduct = packageProducts?.find(
    p => p.corePackage?.interval === 'month'
  ) as PapiProductV3;
  const monthlyPackage = monthlyPackageProduct?.corePackage;

  const yearlyPackageProduct = packageProducts?.find(
    p => p.corePackage?.interval === 'year'
  ) as PapiProductV3;
  const yearlyPackage = yearlyPackageProduct?.corePackage;

  useEffect(() => {
    setSelectedCycle(
      yearlyPackageProduct?.discount || !monthlyPackageProduct?.discount ? 'year' : 'month'
    );
  }, [monthlyPackageProduct, yearlyPackageProduct]);

  if (!monthlyPackageProduct || !yearlyPackageProduct || !monthlyPackage || !yearlyPackage) {
    return null;
  }

  const pickedPackageProduct = packageProducts.find(p => p.corePackage?.interval === selectedCycle);
  const pickedPackage = pickedPackageProduct?.corePackage;
  const isGift = !!giftUser;

  // We only show discounts during a sale (not to confuse discount w/ bulk savings).
  // Discount is displayed for one of the packages only, always giving preference to yearly.
  const shouldPackageShowDiscount = (pkg: PapiProductV3) => {
    return Boolean(
      sale?.cpShowDiscount &&
      pkg.discount &&
      (pkg.corePackage?.interval === 'year' || !yearlyPackageProduct?.discount)
    );
  };
  const anyPackageHasDiscount = packageProducts.reduce(
    (acc, pkg) => (shouldPackageShowDiscount(pkg) ? true : acc),
    false
  );

  const anyPackageHasProration = packageProducts.some(
    pkg => pkg.corePackage?.givesProratedDiscount
  );

  const checkoutUrl = Url.checkoutLink(pickedPackageProduct?.subproductId || 0, giftUser?.userId);
  const gotoCheckout = () => window.location.assign(checkoutUrl);

  const showProrationDisclaimer = anyPackageHasProration;

  const renderYearPackageOption = () => (
    <ListItemCardRadioButton
      withControl
      withThickSelectedBorder
      className={s['radio-button']}
      label={
        <PackageLabel
          corePackage={yearlyPackage}
          showDiscountedPrice={shouldPackageShowDiscount(yearlyPackageProduct)}
        />
      }
      subLabel={
        <PackageSublabel
          corePackage={yearlyPackage}
          showDiscountedPrice={shouldPackageShowDiscount(yearlyPackageProduct)}
        />
      }
      name="billingCycle"
      value="yearly"
      checked={selectedCycle === 'year'}
      onChange={() => setSelectedCycle('year')}
      disabled={yearlyPackageProduct.corePackage?.status === 'disabled'}
      hasAccessibleText
      suffix={
        <PackageSavingsLabel
          corePackageProduct={yearlyPackageProduct}
          anyPackageHasDiscount={anyPackageHasDiscount}
          showDiscountedPrice={shouldPackageShowDiscount(yearlyPackageProduct)}
        />
      }
    />
  );

  const renderMonthPackageOption = () => {
    const isDisabled = monthlyPackage?.status === 'disabled';

    const monthPackageOption = (
      <ListItemCardRadioButton
        withControl
        withThickSelectedBorder
        className={s['radio-button']}
        label={
          <PackageLabel
            corePackage={monthlyPackage}
            showDiscountedPrice={shouldPackageShowDiscount(monthlyPackageProduct)}
          />
        }
        subLabel={
          <PackageSublabel
            corePackage={monthlyPackage}
            showDiscountedPrice={shouldPackageShowDiscount(monthlyPackageProduct)}
          />
        }
        name="billingCycle"
        value="monthly"
        checked={selectedCycle === 'month'}
        onChange={() => setSelectedCycle('month')}
        disabled={isDisabled}
        hasAccessibleText
        suffix={
          <PackageSavingsLabel
            corePackageProduct={monthlyPackageProduct}
            anyPackageHasDiscount={anyPackageHasDiscount}
            showDiscountedPrice={shouldPackageShowDiscount(monthlyPackageProduct)}
          />
        }
      />
    );

    return monthlyPackage?.status === 'disabled' ? (
      <SimpleTooltip
        placement="bottom"
        secondaryPlacement="start"
        text={t('packagePicker.billingCycle.disabled.tooltip')}
        usePortal
        offsetModifier={[0, 8]}
        arrowClassname={s['tooltip-arrow']}
        containerClassName={s['tooltip-content']}
      >
        {monthPackageOption}
      </SimpleTooltip>
    ) : (
      monthPackageOption
    );
  };

  return (
    <DSModal
      alwaysLight
      isOpen={isOpen}
      heading={t('packagePicker.billingCycle.new.heading')}
      subheading={t(`packagePicker.billingCycle.new.subHeading.${coreType}`)}
      onRequestClose={onRequestClose}
      contentClassName={s['content']}
    >
      <div className={s['selection-area']}>
        {renderYearPackageOption()}
        {renderMonthPackageOption()}
      </div>
      <div className={s['custom-footer-area']}>
        <BrandedButton
          variant="brand-green"
          size="large"
          onClick={restrictToLoggedIn(ReferralType.CORE_CYCLE_PICKER, gotoCheckout, checkoutUrl)}
          className={s['cta']}
          biData={
            pickedPackage
              ? BiData<BuyCoreButtonClickFromPPBiEvent>({
                  evid: 500,
                  give_core_or_become_core: alreadyHasSubscription
                    ? 'upgrade_core'
                    : isGift
                      ? 'give_core'
                      : 'become_core',
                  membershiplength: pickedPackage.interval === 'month' ? 1 : 12,
                  membershiptypeid: pickedPackageProduct.subproductId,
                  referral_info: ReferralType.CORE_BILLING_PAYMENTS,
                })
              : undefined
          }
        >
          {t('packagePicker.billingCycle.new.cta.goToCheckout')}
        </BrandedButton>
        <div className={s['disclaimer']}>
          {sale &&
            sale.saleEndDate &&
            anyPackageHasDiscount &&
            t('packagePicker.billingCycle.saleDisclaimer', {
              endDate: formatSaleEndDate(sale.saleEndDate, i18n.language),
            })}
          {t('packagePicker.billingCycle.new.finalPriceDisclaimer')}{' '}
          {showProrationDisclaimer && t('packagePicker.billingCycle.new.prorationDisclaimer')}
        </div>
      </div>
    </DSModal>
  );
};
BillingCyclePopup.displayName = 'BillingCyclePopup';

export default BillingCyclePopup;
